<template>
  <v-container>
    <v-card flat>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <p class="text-body-1 mb-4">
              {{ $t($ts.giftCardManagement.cards.description) }}
            </p>
          </v-col>
        </v-row>

        <v-divider class="my-4" />

        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title>
                {{ $t($ts.giftCardManagement.cards.title) }}
                <v-spacer />
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  :label="$t($ts.giftCardManagement.cards.search)"
                  single-line
                  hide-details
                />
              </v-card-title>
              <v-data-table
                :headers="tableHeaders"
                :items="activeGiftCards"
                :loading="loading"
                :search="search"
                class="elevation-1"
              >
                <template #item.balance="{ item }">
                  {{ formatCurrency(item.balance) }}
                </template>

                <template #item.initialValue="{ item }">
                  {{ formatCurrency(item.initialValue) }}
                </template>

                <template #item.status="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    small
                  >
                    {{ $t($ts.giftCardManagement.cards.statuses[getStatusKey(item.status)]) }}
                  </v-chip>
                </template>

                <template #item.actions="{ item }">
                  <v-icon
                    small
                    class="mr-2"
                    @click="viewDetails(item)"
                  >
                    mdi-eye
                  </v-icon>
                  <v-icon
                    small
                    @click="voidCard(item)"
                  >
                    mdi-cancel
                  </v-icon>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { giftCardsModule } from "@/store/modules/giftCards/giftCardsModule";
import {
  IGiftCardDTO,
  GiftCardStatus,
} from "@shared/types";

@Component({
  components: {},
})
export default class GiftCardsSettings extends Vue {
  search = "";

  get loading() {
    return giftCardsModule.loading;
  }

  get giftCards(): IGiftCardDTO[] {
    return giftCardsModule.cards;
  }

  get activeGiftCards(): IGiftCardDTO[] {
    const activeStatuses = [
      GiftCardStatus.Active,
      GiftCardStatus.Generated,
      GiftCardStatus.Printed,
    ];
    return this.giftCards.filter((card) => activeStatuses.includes(card.status));
  }

  get tableHeaders() {
    return [
      { text: this.$t(this.$ts.giftCardManagement.cards.code).toString(), value: "code" },
      { text: this.$t(this.$ts.giftCardManagement.cards.balance).toString(), value: "balance" },
      { text: this.$t(this.$ts.giftCardManagement.cards.initialValue).toString(), value: "initialValue" },
      { text: this.$t(this.$ts.giftCardManagement.cards.status).toString(), value: "status" },
      { text: this.$t(this.$ts.giftCardManagement.cards.created).toString(), value: "createdAt" },
      { text: this.$t(this.$ts.giftCardManagement.cards.actions).toString(), value: "actions", sortable: false },
    ];
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat("no-NO", {
      style: "currency",
      currency: "NOK",
    }).format(value);
  }

  getStatusColor(status: GiftCardStatus): string {
    const statusColors: Record<GiftCardStatus, string> = {
      [GiftCardStatus.Generated]: "info",
      [GiftCardStatus.Printed]: "info",
      [GiftCardStatus.Active]: "success",
      [GiftCardStatus.PartiallyRedeemed]: "warning",
      [GiftCardStatus.Redeemed]: "grey",
      [GiftCardStatus.Expired]: "error",
      [GiftCardStatus.Voided]: "error",
    };
    return statusColors[status] || "grey";
  }

  getStatusKey(status: GiftCardStatus): string {
    const statusKeys: Record<GiftCardStatus, string> = {
      [GiftCardStatus.Generated]: "generated",
      [GiftCardStatus.Printed]: "printed",
      [GiftCardStatus.Active]: "active",
      [GiftCardStatus.PartiallyRedeemed]: "partiallyRedeemed",
      [GiftCardStatus.Redeemed]: "redeemed",
      [GiftCardStatus.Expired]: "expired",
      [GiftCardStatus.Voided]: "voided",
    };
    return statusKeys[status] || "active";
  }

  viewDetails(item: IGiftCardDTO): void {
    console.log("View details", item);
  }

  voidCard(item: IGiftCardDTO): void {
    console.log("Void card", item);
  }

  async mounted() {
    await giftCardsModule.fetchCards();
  }
}
</script>
